import React, { useCallback, useEffect, useRef, useState } from "react";
import backgroundImg from "../assets/background-img.png";
import pwcLogo from "../assets/pwcLogo.png";
import HRDFooter from "./hrdFooter";
import HRDRedirectContainer from "./hrdRedirectContainer";
import CustomHRDForm from "../views/CustomHRD/customHRD-form";
import CustomHRDLoading from "../views/CustomHRD/customHRD-loading";
import buildAuthorizeUrl from "../api/CustomHRD/buildAuthorizeUrl";
import { getCSRFToken } from "../api/csrfToken";
import getDomainDetails from "../api/CustomHRD/getDomainDetails";
import getAuthorizeParams from "../hooks/CustomHRD/getAuthorizeParams";
import { useTranslation } from 'react-i18next';
import getLegalLinks from "../helpers/getLegalLinks";

const HRDContainer = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [showFallbackError, setShowFallbackError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const legalLinks = getLegalLinks();

  const abortControllerRef = useRef(null);
  const isCancelledRef = useRef(false);
  const redirectTimeoutRef = useRef(null);

  const [isRedirecting, setIsRedirecting] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const gotoUrl = params.get("goto");
    

    if (!gotoUrl) return false;
    

    try {
      const decodedGotoUrl = decodeURIComponent(gotoUrl);
      const goto = new URL(decodedGotoUrl);
      
      return !!(
  goto.searchParams.get("login_hint") || goto.searchParams.get("email")
);
    
} catch {
      return false;
    }
  });

  const authorizeParams = getAuthorizeParams();

  const isValidEmail = (value) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(value);
  };

  const handleEmailChange = (event) => {
    setEmail(event.target.value);
    setEmailError("");
    setShowFallbackError(false);
  };

  const handleCancel = () => {
    isCancelledRef.current = true;

    if (redirectTimeoutRef.current) {
      clearTimeout(redirectTimeoutRef.current);
      redirectTimeoutRef.current = null;
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    window.stop();

    setIsLoading(false);
    setIsRedirecting(false);
    setShowFallbackError(false);
  };

  const processDomainFlow = useCallback(
    async (emailValue, hideScreen = false) => {
      const controller = new AbortController();

      abortControllerRef.current = controller;
      isCancelledRef.current = false;

      try {
        if (hideScreen) {
          setIsRedirecting(true);
        } else {
          setIsLoading(true);
        }

        const tokenResponse = await getCSRFToken(controller.signal);

        if (isCancelledRef.current) return;

        const domainResponse = await getDomainDetails(
          {
            email: emailValue,
            productId: authorizeParams.clientId || "",
            isAutoRedirect: hideScreen,
          },
          tokenResponse.data.csrfToken,
          controller.signal
        );

        if (isCancelledRef.current) return;

        const authorizeUrl = buildAuthorizeUrl({
          gotoUrl: authorizeParams.gotoUrl,
          email: emailValue,
          domainType: domainResponse.data.domainType,
        });
       

        if (isCancelledRef.current) return;

        redirectTimeoutRef.current = setTimeout(() => {
          redirectTimeoutRef.current = null;
          if (!isCancelledRef.current) {
            window.location.assign(authorizeUrl);
          }
        }, 0);
      } catch (error) {
        if (
          isCancelledRef.current ||
          error.name === "CanceledError" ||
          error.code === "ERR_CANCELED"
        ) {
          return;
        }

        setIsRedirecting(false);
        setIsLoading(false);
        setShowFallbackError(true);
      }

    },
    [authorizeParams.gotoUrl]
  );

  const handleNext = async () => {
    const trimmedEmail = email.trim();

    if (!isValidEmail(trimmedEmail)) {
      setEmailError(t("customHRD.invalidEmailMessage"));
      return;
    }

    await processDomainFlow(trimmedEmail, false);
  };

  useEffect(() => {
    if (!authorizeParams.isValid) {
      setShowFallbackError(true);
      return;
    }

    if (!authorizeParams.loginHint) {
      return;
    }

    const trimmedEmail = authorizeParams.loginHint.trim();

    if (!isValidEmail(trimmedEmail)) {
      setIsRedirecting(false);
      setShowFallbackError(true);
      return;
    }

    processDomainFlow(trimmedEmail, true);
  }, [authorizeParams.isValid, authorizeParams.loginHint, processDomainFlow]);

  useEffect(() => {
    document.title = isRedirecting
      ? t("customHRD.tabs.redirecting")
      : t("customHRD.tabs.mainLogin");
  }, [isRedirecting, t]);

  useEffect(() => {
    return () => {
      if (redirectTimeoutRef.current) {
        clearTimeout(redirectTimeoutRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  if (isRedirecting) {
    return <HRDRedirectContainer />;
  }

  return (
    <div className="hrd-page">
      <div
        className="hrd-background"
        style={{ backgroundImage: `url(${backgroundImg})` }}
      />

      <main className="hrd-main">
        <section className="hrd-card" aria-label={t("customHRD.signInTitle")}>
          {isLoading ? (
            <>
              <CustomHRDLoading onCancel={handleCancel} />
              <div className="hrd-loading-spacer" />
            </>
          ) : (
            <>
              <img
                src={pwcLogo}
                alt={t("customHRD.pwcLogoAlt")}
                style={{ height: "24px" }}
              />

              {showFallbackError ? (
                <>
                  <h1 className="title">{t("customHRD.fallbackTitle")}</h1>

                  <p className="hrd-error-message">
                   {t("customHRD.fallbackTryAgain")}
                  </p>
                </>
              ) : (
                <>
                 <h1 className="title">{t("customHRD.signInTitle")}</h1>

                  <CustomHRDForm
                    email={email}
                    emailError={emailError}
                    handleEmailChange={handleEmailChange}
                    handleNext={handleNext}
                  />
                </>
              )}

              <div className="hrd-terms-container">
                <p className="hrd-terms-text">
                {t("customHRD.termsTextBeforeLink")}{" "}
                <a
                href={legalLinks.termsOfUse}
                 target="_blank"
                 rel="noreferrer"
                  className="hrd-link"
                >
                 {t("customHRD.termsOfUse")}
                  </a>
                , {t("customHRD.termsTextAfterLink")}
                </p>
              </div>
            </>
          )}
        </section>
      </main>

      <HRDFooter />
    </div>
  );
};

export default HRDContainer;